/**
 * `W5-T01` §4.2, §4.3 — what every payouts screen shares: reading the status, asking for a link, and
 * what a refusal means. Pure where it can be, and keys, never sentences.
 *
 * **Leaving for Stripe is a redirect, not a `window` call.** React Router turns a `redirect()` to
 * another origin, from an action or a loader, into `window.location.assign(url)`. So the routes
 * return one, and nothing here touches a browser global (ADR-011 R2). An Account Link is single-use
 * and expires in minutes, so it is asked for on the click, never cached, and never logged.
 */
import { redirect, type LoaderFunctionArgs } from 'react-router';
import {
  PayoutOnboardingLinkInputSchema,
  type PayoutOnboardingLinkInput,
  type PayoutStatus,
  type PayoutStatusCode,
} from '@marketplace/contracts';
import { ApiError } from '../../shared/api-error.js';
import { queryKeys, routeContext } from '../../shared/query.js';

export type OnboardingFrom = PayoutOnboardingLinkInput['from'];

/** The statuses whose next step is the provider's, at Stripe (§4.3). The rest wait or are closed. */
const CONTINUES: readonly PayoutStatusCode[] = ['NOT_STARTED', 'INCOMPLETE', 'ACTION_REQUIRED'];

/** The statuses that can change while the provider waits on the return page (§4.3). */
const WATCHED: readonly PayoutStatusCode[] = ['INCOMPLETE', 'IN_REVIEW'];

export function canContinue(status: PayoutStatusCode): boolean {
  return CONTINUES.includes(status);
}

export function isWatched(status: PayoutStatusCode): boolean {
  return WATCHED.includes(status);
}

/**
 * Where the provider came from, from the closed set, or `fallback`. The refresh page reads it from a
 * URL anybody can type, so anything else is not an error: it is the account page.
 */
export function fromOf(value: unknown, fallback: OnboardingFrom = 'account'): OnboardingFrom {
  const parsed = PayoutOnboardingLinkInputSchema.shape.from.safeParse(value);
  return parsed.success ? parsed.data : fallback;
}

/**
 * The status, or `null` when it could not be read.
 *
 * `null` is no banner, never a page error (§4.3): a client opening the feed (`403`), a profile not
 * finished (`409`) and an API that failed all leave the page what it was, and the quote or bid route
 * stays the judge. `fresh` is for the payouts pages, which exist to show the latest answer; the feed
 * pages poll every five seconds and take the cache.
 */
export async function loadMyPayouts(
  context: LoaderFunctionArgs['context'],
  { fresh = false }: { fresh?: boolean } = {},
): Promise<PayoutStatus | null> {
  const { queryClient, api } = context.get(routeContext);
  return queryClient
    .fetchQuery({
      queryKey: queryKeys.myPayouts(),
      queryFn: () => api.getMyPayouts(),
      ...(fresh ? { staleTime: 0 } : {}),
    })
    .catch(() => null);
}

export type OnboardingFailure = 'payouts.status.REJECTED' | 'payouts.error.unavailable';

/** Where a provider with no profile goes: the API's `409 CONFLICT`, and a `403` for a client. */
export function becomeAProPath(locale: string): string {
  return `/${locale}/become-a-pro`;
}

/**
 * A refused link, as what the provider reads.
 *
 * `PAYOUTS_ONBOARDING_CLOSED` is Stripe having rejected the account: the status line says to contact
 * us (§7). Anything else, `503 PAYMENTS_UNAVAILABLE` included, is "we could not reach Stripe".
 */
export function onboardingFailureKey(error: unknown): OnboardingFailure {
  if (error instanceof ApiError && error.message === 'PAYOUTS_ONBOARDING_CLOSED') {
    return 'payouts.status.REJECTED';
  }
  return 'payouts.error.unavailable';
}

/**
 * The server already checks that the link is Stripe's (§4.2). Checking again here costs one line and
 * means this page can never be the one that sends a provider somewhere else.
 */
function isStripeLink(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && parsed.hostname === 'connect.stripe.com';
  } catch {
    return false;
  }
}

/**
 * Ask for a link and go to it: the redirect to Stripe, the redirect to `become-a-pro` for somebody
 * with no profile, or the failure to show.
 */
export async function startOnboarding(
  context: LoaderFunctionArgs['context'],
  locale: string,
  from: OnboardingFrom,
): Promise<Response | { failure: OnboardingFailure }> {
  const { queryClient, api } = context.get(routeContext);
  try {
    const { url } = await api.createPayoutOnboardingLink(from);
    if (!isStripeLink(url)) return { failure: 'payouts.error.unavailable' };
    return redirect(url);
  } catch (error) {
    if (error instanceof ApiError && (error.status === 403 || error.message === 'CONFLICT')) {
      return redirect(becomeAProPath(locale));
    }
    // A closed onboarding means the status changed under the page; the next read must show it.
    await queryClient.invalidateQueries({ queryKey: queryKeys.myPayouts(), refetchType: 'all' });
    return { failure: onboardingFailureKey(error) };
  }
}

/**
 * `PAYOUTS_NOT_READY` on a quote or bid (§7): the CTA line's sentence as the form's error, or `null`
 * for any other refusal, which the form's own mapping reads.
 */
export function payoutsRefusalKey(error: unknown): 'payouts.pending.cta' | null {
  return error instanceof ApiError && error.message === 'PAYOUTS_NOT_READY'
    ? 'payouts.pending.cta'
    : null;
}
